import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2, Power, Loader2, Mail, Phone, Pencil, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDateBR } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Filters } from "../components/Filters";
import { ImpersonateButton } from "../components/ImpersonateButton";
import { UserEditDialog } from "../components/UserEditDialog";
import { ConfirmDelete } from "../components/ConfirmDelete";
import { GRANTABLE } from "../lib/perms";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const emptyForm = { name: "", email: "", password: "", nivel: "Gestor", phone: "", permissions: ["painel", "extrato", "colaboradores", "ponto", "relatorios"] };

export default function Gestores() {
    const qc = useQueryClient();
    const [filters, setFilters] = useState({ q: "", start: "", end: "" });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [editUser, setEditUser] = useState(null);

    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);

    const { data: list = [] } = useQuery({ queryKey: ["gestores", filters], queryFn: async () => (await api.get(`/gestores?${params}`)).data });

    const togglePerm = (k, c) => setForm((f) => ({ ...f, permissions: c ? Array.from(new Set([...f.permissions, k])) : f.permissions.filter((x) => x !== k) }));

    const create = async () => {
        if (!form.name || !form.email || !form.password) { toast.error("Preencha nome, e-mail e senha"); return; }
        setSaving(true);
        try {
            await api.post("/gestores", form);
            qc.invalidateQueries({ queryKey: ["gestores"] });
            setOpen(false);
            setForm(emptyForm);
            toast.success("Gestor cadastrado");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (g) => {
        try {
            await api.put(`/gestores/${g.user_id}`, { active: !g.active });
            qc.invalidateQueries({ queryKey: ["gestores"] });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/gestores/${id}`);
            qc.invalidateQueries({ queryKey: ["gestores"] });
            toast.success("Gestor removido");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader
                overline="Equipe de gestão"
                title="Gestores"
                description="Cadastre e gerencie os gestores da casa e defina o nível de acesso de cada um. Apenas o Master Geral acessa esta área."
                action={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-full px-6" data-testid="add-gestor-btn"><UserPlus className="mr-2 h-4 w-4" /> Novo gestor</Button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg">
                            <DialogHeader><DialogTitle className="font-heading text-2xl font-medium">Cadastrar gestor</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Nome completo</Label>
                                    <Input data-testid="gestor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" placeholder="Ex: Carlos Souza" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Nível / Cargo</Label>
                                        <Input data-testid="gestor-nivel" value={form.nivel} onChange={(e) => setForm({ ...form, nivel: e.target.value })} className="mt-1 rounded-xl" placeholder="Ex: Gestor Financeiro" />
                                    </div>
                                    <div>
                                        <Label>Telefone</Label>
                                        <Input data-testid="gestor-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" placeholder="(11) 90000-0000" />
                                    </div>
                                </div>
                                <div>
                                    <Label>E-mail de acesso</Label>
                                    <Input data-testid="gestor-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-xl" placeholder="gestor@email.com" />
                                </div>
                                <div>
                                    <Label>Senha inicial</Label>
                                    <Input data-testid="gestor-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" placeholder="Defina uma senha" />
                                </div>
                                <div>
                                    <Label>Permissões de acesso</Label>
                                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        {GRANTABLE.map((f) => (
                                            <div key={f.key} className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/30 px-3 py-2">
                                                <span className="text-sm">{f.label}</span>
                                                <Switch data-testid={`gestor-perm-${f.key}`} checked={form.permissions.includes(f.key)} onCheckedChange={(c) => togglePerm(f.key, c)} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <Button onClick={create} disabled={saving} className="w-full rounded-full" data-testid="save-gestor-btn">
                                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cadastrar
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                }
            />

            <Filters q={filters.q} start={filters.start} end={filters.end} onChange={setFilters} placeholder="Buscar por nome, e-mail ou cargo..." />

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="gestores-grid">
                {list.length === 0 && <p className="text-sm text-muted-foreground">Nenhum gestor cadastrado.</p>}
                {list.map((g) => (
                    <div key={g.user_id} className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] fade-up" data-testid={`gestor-card-${g.user_id}`}>
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl text-lg font-semibold ${g.is_master ? "bg-secondary text-secondary-foreground" : "bg-primary/10 text-primary"}`}>
                                    {g.is_master ? <Crown className="h-5 w-5" /> : g.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-semibold">{g.name}</p>
                                    <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{g.is_master ? "Master Geral" : g.nivel}</span>
                                </div>
                            </div>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${g.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                                {g.active ? "Ativo" : "Inativo"}
                            </span>
                        </div>
                        <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                            <p className="flex items-center gap-2"><Mail className="h-4 w-4" /> {g.email}</p>
                            {g.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4" /> {g.phone}</p>}
                            <p className="text-xs">Desde {formatDateBR(g.created_at)}</p>
                        </div>
                        {g.is_master ? (
                            <div className="mt-4 border-t border-border/60 pt-4 text-center text-xs text-muted-foreground">Acesso total — não editável</div>
                        ) : (
                            <div className="mt-4 flex flex-wrap gap-2 border-t border-border/60 pt-4">
                                <button onClick={() => setEditUser(g)} data-testid={`edit-gestor-${g.user_id}`} className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-muted">
                                    <Pencil className="h-4 w-4" /> Editar
                                </button>
                                <button onClick={() => toggleActive(g)} data-testid={`toggle-gestor-${g.user_id}`} className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-muted">
                                    <Power className="h-4 w-4" /> {g.active ? "Desativar" : "Ativar"}
                                </button>
                                <ImpersonateButton userId={g.user_id} className="px-3" />
                                <ConfirmDelete onConfirm={() => remove(g.user_id)} testid={`delete-gestor-${g.user_id}`} className="ml-auto" title="Excluir gestor" description={`Remover ${g.name}? Esta ação não pode ser desfeita.`} />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {editUser && (
                <UserEditDialog user={editUser} endpoint="/gestores" open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }} onSaved={() => qc.invalidateQueries({ queryKey: ["gestores"] })} />
            )}
        </div>
    );
}
